import { FC, KeyboardEventHandler } from 'react'

import styles from './ConfirmModal.module.scss'

import Button, { ButtonVariants } from '~elements/Button'
import Box from '~lib/ui-kit/Box'
import Modal, { ModalProps } from '~lib/ui-kit/Modal'
import Space from '~lib/ui-kit/Space'
import Text from '~lib/ui-kit/Text'

export interface ConfirmProps {
  label: string
  description?: string
  closeText?: string
  submitText?: string
  submitLoading?: boolean
  variant?: ButtonVariants
  children?: React.ReactNode
  onClose?: () => void
  onSubmit: () => void | Promise<void>
  onKeyDown?: KeyboardEventHandler<HTMLButtonElement>
}

const Confirm: FC<React.PropsWithChildren<ConfirmProps>> = ({
  label,
  description,
  closeText,
  submitText,
  submitLoading,
  variant = 'danger',
  children,
  onClose,
  onSubmit,
}) => {
  return (
    <Box className={styles.root}>
      <Text as="h3" type="heading-l" className="mb-3">
        {label}
      </Text>

      {description && (
        <Text type="body-m" className="mb-6">
          {description}
        </Text>
      )}

      {children && <div className="mb-4">{children}</div>}

      <Space align="center" justify="space-between">
        <Button variant="default" size="l" onClick={onClose} className={styles.control}>
          {closeText}
        </Button>

        <Button
          variant={variant}
          size="l"
          onClick={onSubmit}
          loading={submitLoading}
          autoFocus
          className={styles.control}
        >
          {submitText}
        </Button>
      </Space>
    </Box>
  )
}

export type ConfirmModalProps = ModalProps & ConfirmProps

const ConfirmModal: FC<React.PropsWithChildren<ConfirmModalProps>> = ({
  label,
  description,
  closeText,
  submitText,
  submitLoading,
  variant = 'danger',
  children,
  onClose,
  onSubmit,
  onKeyDown,
  opened,
  ...modalProps
}) => {
  return (
    <Modal opened={opened} {...modalProps}>
      {opened && (
        <Confirm
          label={label}
          description={description}
          closeText={closeText}
          submitText={submitText}
          submitLoading={submitLoading}
          variant={variant}
          onClose={onClose}
          onSubmit={onSubmit}
          onKeyDown={onKeyDown}
        >
          {children}
        </Confirm>
      )}
    </Modal>
  )
}

export default ConfirmModal
